import React from 'react';

import { Container as RadixContainer } from '@radix-ui/themes';
import clsx from 'clsx';

export type ContainerSize =
  | 'none'
  | 'expand'
  | '3xsmall'
  | '2xsmall'
  | 'xsmall'
  | 'small'
  | 'medium'
  | 'default'
  | 'large'
  | 'xlarge'
  | '2xlarge'
  | '3xlarge';

export type ContainerPadding =
  | 'none'
  | '3xsmall'
  | '2xsmall'
  | 'xsmall'
  | 'small'
  | 'medium'
  | 'default'
  | 'large'
  | 'xlarge'
  | '2xlarge'
  | '3xlarge';

interface CustomContainerProps {
  /**
   * Semantic max-width for the container.
   * Default: 'large'
   */
  size?: ContainerSize;
  /**
   * Horizontal padding (left/right) for the container.
   * Default: 'default'
   */
  padding?: ContainerPadding;
}

// Pass all other props to RadixContainer
export type ContainerProps = CustomContainerProps &
  Omit<React.ComponentPropsWithoutRef<typeof RadixContainer>, 'size'>;

const sizeClassMap: Record<ContainerSize, string> = {
  none: 'container--size-none',
  expand: 'container--size-expand',
  '3xsmall': 'container--size-3xsmall',
  '2xsmall': 'container--size-2xsmall',
  xsmall: 'container--size-xsmall',
  small: 'container--size-small',
  medium: 'container--size-medium',
  default: 'container--size-default',
  large: 'container--size-large',
  xlarge: 'container--size-xlarge',
  '2xlarge': 'container--size-2xlarge',
  '3xlarge': 'container--size-3xlarge',
};

const paddingClassMap: Record<ContainerPadding, string> = {
  none: 'container--padding-none',
  '3xsmall': 'container--padding-3xsmall',
  '2xsmall': 'container--padding-2xsmall',
  xsmall: 'container--padding-xsmall',
  small: 'container--padding-small',
  medium: 'container--padding-medium',
  default: 'container--padding-default',
  large: 'container--padding-large',
  xlarge: 'container--padding-xlarge',
  '2xlarge': 'container--padding-2xlarge',
  '3xlarge': 'container--padding-3xlarge',
};

const Container: React.FC<ContainerProps> = ({
  children,
  className,
  size = 'large',
  padding = 'default',
  asChild,
  ...rest
}) => {
  // For none: remove max-width and padding
  // For expand: remove max-width, padding is managed separately
  const isNone = size === 'none';
  const isExpand = size === 'expand';
  return (
    <RadixContainer
      className={clsx(
        'container',
        sizeClassMap[size],
        isNone || isExpand ? null : paddingClassMap[padding],
        isNone && paddingClassMap['none'],
        className
      )}
      asChild={asChild}
      {...rest}
    >
      {children}
    </RadixContainer>
  );
};

export { Container };
